/**
 * Signed-in user and public app configuration.
 *
 * When the server cannot be reached (offline), the last known user is read
 * from this device so the communication board keeps working; account pages
 * still require a connection.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AppConfig, PublicUser } from '@shared/api';
import { ApiError, get, post } from '../lib/api';
import * as storage from '../lib/storage';

interface SessionState {
  status: 'loading' | 'ready';
  user: PublicUser | null;
  /** True when `user` came from the device cache because the server is unreachable. */
  offline: boolean;
  config: AppConfig | null;
  refresh(): Promise<PublicUser | null>;
  setUser(user: PublicUser): void;
  logout(): Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);
const USER_KEY = 'user';

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [user, setUserState] = useState<PublicUser | null>(null);
  const [offline, setOffline] = useState(false);
  const [config, setConfig] = useState<AppConfig | null>(null);

  const setUser = useCallback((next: PublicUser) => {
    setUserState(next);
    setOffline(false);
    storage.writeJson(USER_KEY, next);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { user: me } = await get<{ user: PublicUser }>('/api/auth/me');
      setUser(me);
      return me;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUserState(null);
        setOffline(false);
        storage.remove(USER_KEY);
        return null;
      }
      // Network problem: keep working with the last known user on this device.
      const cached = storage.readJson<PublicUser>(USER_KEY);
      setUserState(cached);
      setOffline(!!cached);
      return cached;
    } finally {
      setStatus('ready');
    }
  }, [setUser]);

  const logout = useCallback(async () => {
    await post('/api/auth/logout').catch(() => {});
    // Personal data cached on this device is removed on logout.
    storage.clearAll();
    setUserState(null);
    setOffline(false);
  }, []);

  useEffect(() => {
    void refresh();
    get<AppConfig>('/api/config').then(setConfig, () => setConfig(null));
  }, [refresh]);

  useEffect(() => {
    const onOnline = () => offline && void refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [offline, refresh]);

  const value = useMemo(
    () => ({ status, user, offline, config, refresh, setUser, logout }),
    [status, user, offline, config, refresh, setUser, logout],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
